import { Box, Grid, IconButtonProps, Paper, useMediaQuery, useTheme } from '@mui/material';
import '../Calendar.scss';
import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { withAuthInfo } from '@propelauth/react';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import {
  CalculatedUserAvailabilityOut,
  EmployeesService,
  EnrichedEventData,
  EntityType,
  EventService,
  NoteData,
  OrderData,
  OrderService,
  PrivateSlotOut,
  ShiftOut,
  UnavailabilityData,
  UserService,
  WaitingListItemData,
} from '../../api';
import { useCalendar } from '../CalendarContext/CalendarContext';
import EditOrderDialog from '../dialogs/EditOrderDialog/EditOrderDialog';
import ConfirmationDialog from '../../common/ui/ConfimationDialog';
import CalendarSpeedDial from '../components/CalendarSpeedDial';
import ShiftDialog from '../dialogs/ShiftDialog';
import { useShifts } from '../swr/useShifts';
import { useUnavailabilities } from '../swr/useUnavailabilities';
import { useOrders } from '../swr/useOrders';
import { useEvents } from '../swr/useEvents';
import { CalendarContent } from '../CalendarContent';
import Swipe from '../../common/Swipe';
import { useCalendarUserAvailabilities } from '../swr/useCalendarUserAvailabilities';
import { UnvailabilityDeletionConfirmation } from '../dialogs/PrivateExperianceAvailability/PrivateUnavailability/UnvailabilityDeletionConfirmation';
import { useNotes } from '../swr/useNotes';
import NoteDialog from '../dialogs/NoteDialog/NoteDialog';
import { useWaitingList } from '../swr/useWaitingList';
import { ExperienceAvailabilityDrawerWrapper } from '../dialogs/PrivateExperianceAvailability/ExperienceAvailabilityDrawerWrapper';
import { usePrivateSlotsAvailability } from '../swr/usePrivateSlotsAvailability';
import { DialogState, RecurrentUpdateDialog } from '../components/RecurrentUpdateDialog';
import { calculateFetchGap } from '../CalendarContext/calculateFetchGap';
import { CalendarToolbar } from './CalendarToolbar';
import { UseAuthPermissions } from '../../session/UseAuthPermissions';
// deprecated use .const.ts
export const DATE_FORMAT = 'YYYY-MM-DD';
// deprecated use .const.ts

export const TIME_FORMAT = 'HH:mm';

interface ExpandMoreProps extends IconButtonProps {
  isExpanded: boolean;
  expandUp?: boolean;
}

export const ExpandMore = styled((props: ExpandMoreProps) => {
  const { ...other } = props;
  return <IconButton {...other} />;
})(({ theme, isExpanded, expandUp }) => ({
  transform: isExpanded ? `rotate(${expandUp ? 180 : 0}deg)` : `rotate(${expandUp ? 0 : 180}deg)`,
  marginLeft: 'auto',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}));

export enum CalendarView {
  DAY = 'day',
  THREE_DAY = '3day',
  WEEK = 'week',
  MONTH = 'month',
  CONDENSED = 'condensed',
}

export const mapCalendarViewToNumOfDays = (calendarView: CalendarView | null): number => {
  if (!calendarView) {
    return 7;
  }
  switch (calendarView) {
    case CalendarView.DAY:
      return 1;
    case CalendarView.THREE_DAY:
      return 3;
    case CalendarView.WEEK:
      return 7;
    case CalendarView.MONTH:
      return 30;
    default:
      return 7;
  }
};

export const getTodayStartsOfWeek = () => {
  return moment().startOf('week').format(DATE_FORMAT);
};

export const getTodayStartsOfMonth = () => {
  return moment().startOf('month').format(DATE_FORMAT);
};

export type CalendarItems = {
  shifts: Record<string, Array<ShiftOut>>;
  unavailabilities: Record<string, Array<UnavailabilityData>>;
  orders: Record<string, Array<OrderData>>;
  events: Record<string, Array<EnrichedEventData>>;
  calendarUserAvailabilities: Record<string, Array<CalculatedUserAvailabilityOut>>;
  notes: Record<string, Array<NoteData>>;
  waitingList: Record<string, Array<WaitingListItemData>>;
  privateSlotAvailabilities: Record<string, Array<PrivateSlotOut>>;
};
const CalendarWrapper = () => {
  const theme = useTheme();
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('lg'));
  const { readOnly } = UseAuthPermissions();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const { t, i18n } = useTranslation();

  const [isNoteDialogOpen, setNoteDialogOpen] = useState(false);
  const [isShiftDialogOpen, setShiftDialogOpen] = useState(false);
  const [calendarLoading, setCalendarLoading] = useState(true);

  const [speedDialOpen, setSpeedDialOpen] = useState(false);
  const [isEventBoxOpen, setIsEventBoxOpen] = useState(false);

  const previousNextCallbacks = {} as {
    previous: any;
    next: any;
  };

  const {
    editOrderDrawerState,
    closeEditOrderDrawer,
    deleteOrderDialogState,
    closeDeleteOrderDialog,
    deleteEventDialogState,
    closeDeleteEventDialog,
    deleteShiftDialogState,
    closeDeleteShiftDialog,
    editShiftDialogState,
    closeEditShiftDialog,
    setUsers,
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    shouldBlockSwiping,
    isAnyThingOpen,
    calendarView,
    currentDate,
  } = useCalendar();

  useEffect(() => {
    // with month view we will be always missing new days on carusel navigation so we should refetch
    if (
      moment(currentDate) <= moment(startDate) ||
      moment(currentDate) >= moment(endDate) ||
      calendarView === CalendarView.MONTH
    ) {
      const [newStartDay, newEndDay] = calculateFetchGap(currentDate, calendarView);
      setStartDate(newStartDay.format(DATE_FORMAT));
      setEndDate(newEndDay.format(DATE_FORMAT));
    }
  }, [currentDate]);

  useEffect(() => {
    const [newStartDay, newEndDay] = calculateFetchGap(currentDate, calendarView);
    setStartDate(newStartDay.format(DATE_FORMAT));
    setEndDate(newEndDay.format(DATE_FORMAT));
  }, [calendarView]);

  const { isLoading: isLoadingCalendarUserAvailabilities, items: calendarUserAvailabilities } =
    useCalendarUserAvailabilities({
      startDate,
      endDate,
    });
  const { isLoading: isLoadingShifts, items: shifts, deleteItem: deleteShift } = useShifts({ startDate, endDate });
  const {
    isLoading: isLoadingUnavailabilities,
    items: unavailabilities,
    deleteItem: deleteUnavailability,
    reFetch: reFetchUnavailabilities,
  } = useUnavailabilities({ startDate, endDate });

  const { isLoading: isLoadingNotes, items: notes } = useNotes({ startDate, endDate });
  const {
    isLoading: isLoadingOrders,
    items: orders,
    deleteItem: deleteOrder,
    addItem: addOrder,
    updateItem: updateOrder,
  } = useOrders({ startDate, endDate });

  const {
    isLoading: isLoadingEvents,
    items: events,
    deleteItem: deleteEvent,
    reFetch: reFetchEvents,
  } = useEvents({ startDate, endDate });

  const { isLoading: isLoadingWaitingList, items: waitingList } = useWaitingList({ startDate, endDate });
  const { isLoading: isLoadingPrivateSlotAvailability, items: privateSlotAvailabilities } = usePrivateSlotsAvailability(
    {
      startDate,
      endDate,
    },
  );

  useEffect(() => {
    setCalendarLoading(
      isLoadingShifts ||
        isLoadingUnavailabilities ||
        isLoadingOrders ||
        isLoadingNotes ||
        isLoadingEvents ||
        isLoadingCalendarUserAvailabilities ||
        isLoadingWaitingList ||
        isLoadingPrivateSlotAvailability,
    );
  }, [
    isLoadingShifts,
    isLoadingUnavailabilities,
    isLoadingOrders,
    isLoadingNotes,
    isLoadingEvents,
    isLoadingCalendarUserAvailabilities,
    isLoadingWaitingList,
    isLoadingPrivateSlotAvailability,
  ]);

  const calendarItems: CalendarItems = {
    shifts,
    unavailabilities,
    orders,
    events,
    calendarUserAvailabilities,
    notes,
    waitingList,
    privateSlotAvailabilities,
  };

  useEffect(() => {
    localStorage.setItem('webook-calendarview', calendarView || '');
  }, [calendarView]);

  useEffect(() => {
    const fetchUsers = async () => {
      const users = await UserService.getUsers();
      if (setUsers) {
        setUsers(users);
      }
    };
    void fetchUsers();
  }, []);

  const setShiftOpenInternal = (val: any) => {
    setShiftDialogOpen(val);
    closeEditShiftDialog();
  };
  // TODO find better solution. this is used to prevent swipes
  const isAnyDialogOpen = () => {
    return isAnyThingOpen || isEventBoxOpen;
  };

  const onSwipeLeft = () => {
    if (isAnyDialogOpen() || calendarView === CalendarView.CONDENSED) {
      return;
    }
    if (i18n.dir() === 'rtl') {
      previousNextCallbacks.previous();
    } else {
      previousNextCallbacks.next();
    }
  };

  const onSwipeRight = () => {
    if (isAnyDialogOpen() || calendarView === CalendarView.CONDENSED) {
      return;
    }
    if (i18n.dir() === 'rtl') {
      previousNextCallbacks.next();
    } else {
      previousNextCallbacks.previous();
    }
  };

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>Monkeybook - {t('main_sidebar.calendar')}</title>
        </Helmet>
      </HelmetProvider>

      <ExperienceAvailabilityDrawerWrapper />

      {isNoteDialogOpen && (
        <NoteDialog
          open={isNoteDialogOpen}
          setOpen={setNoteDialogOpen}
          calendarEndDate={endDate}
          calendarStartDate={startDate}
        />
      )}
      {(isShiftDialogOpen || editShiftDialogState?.isOpen) && (
        <ShiftDialog
          open={isShiftDialogOpen || editShiftDialogState?.isOpen}
          setOpen={setShiftOpenInternal}
          shift={editShiftDialogState?.shift}
          startDate={startDate}
          endDate={endDate}
          createNewShiftPayload={editShiftDialogState.createNewShiftPayload}
        />
      )}
      <EditOrderDialog
        editOrderDialogState={editOrderDrawerState}
        closeEditOrderDialog={closeEditOrderDrawer}
        addOrder={async (orderToUpsert: OrderData) => {
          if (orderToUpsert.parent_id) {
            await reFetchEvents();
          } else {
            await addOrder(orderToUpsert);
          }
        }}
        updateOrder={updateOrder}
      />

      <ConfirmationDialog
        title={t('calendar.delete_order.title')}
        content={<Typography>{t('calendar.delete_order.description')}</Typography>}
        isOpen={deleteOrderDialogState?.isOpen}
        onClose={async (actionConfirmed) => {
          if (actionConfirmed) {
            await OrderService.deleteOrder(deleteOrderDialogState.order.id);
            if (!deleteOrderDialogState.order.parent_id) {
              deleteOrder(deleteOrderDialogState.order);
            }

            if (deleteOrderDialogState.onSubmitCallback) {
              deleteOrderDialogState.onSubmitCallback();
            }
            closeDeleteOrderDialog();
          } else {
            closeDeleteOrderDialog();
          }
        }}
      />
      <RecurrentUpdateDialog
        dialogState={
          deleteEventDialogState?.isOpen && deleteEventDialogState?.event?.recurrent_event_id
            ? DialogState.DELETE
            : DialogState.CLOSED
        }
        setDialogStatus={closeDeleteEventDialog}
        recurrentId={deleteEventDialogState?.event?.recurrent_event_id}
        recurrentEntityType={EntityType.EVENT}
        // no delete option in this component
        deleteSingle={async () => {
          await EventService.deleteEvent(deleteEventDialogState?.event.id);
          deleteEvent(deleteEventDialogState?.event);
          closeDeleteEventDialog();
        }}
        deleteReccurrent={async () => {
          await EventService.deleteEventRecurrent(
            deleteEventDialogState?.event.recurrent_event_id,
            deleteEventDialogState?.event.date,
          );
          await reFetchEvents();
          closeDeleteEventDialog();
        }}
        fromDate={deleteEventDialogState?.event?.date}
        updateSingle={async () => {}}
        updateRecurrent={async () => {}}
        texts={{
          update: {
            cta: t('calendar.edit_event.edit_cta'),
            title: t('calendar.edit_event.edit_title_questions'),
            singleOption: t('calendar.edit_event.singleUpdate'),
            recurrentOption: t('calendar.edit_event.repeatingUpdate'),
            recurrentCount: t('calendar.edit_event.recurrent_count'),
          },
          delete: {
            title: t('calendar.edit_event.deletetitle'),
            singleOption: t('calendar.edit_event.single'),
            recurrentOption: t('calendar.edit_event.repeating'),
            recurrentCount: t('calendar.edit_event.recurrent_count'),
          },
        }}
      />
      <ConfirmationDialog
        title={t('calendar.delete_event.title')}
        content={<Typography>{t('calendar.delete_event.description')}</Typography>}
        isOpen={deleteEventDialogState?.isOpen && !deleteEventDialogState?.event?.recurrent_event_id}
        onClose={async (actionConfirmed) => {
          if (actionConfirmed) {
            await EventService.deleteEvent(deleteEventDialogState?.event.id);
            deleteEvent(deleteEventDialogState?.event);
          }
          closeDeleteEventDialog();
        }}
      />
      <UnvailabilityDeletionConfirmation
        reFetchUnavailabilities={reFetchUnavailabilities}
        deleteUnavailability={deleteUnavailability}
      />

      <ConfirmationDialog
        title={t('calendar.delete_shift.title')}
        content={<Typography>{t('calendar.delete_shift.description')}</Typography>}
        isOpen={deleteShiftDialogState?.isOpen}
        onClose={async (actionConfirmed) => {
          closeDeleteShiftDialog();
          if (actionConfirmed) {
            const shiftToDelete = deleteShiftDialogState.shift;
            await EmployeesService.deleteShift(shiftToDelete.id);
            deleteShift(shiftToDelete);
          }
        }}
      />
      <Grid sx={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto' }} gap={1}>
        <Paper className="calendar" sx={{ overflowX: 'auto', flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: 'background.paper' }}>
            <CalendarToolbar
              setNoteDialogOpen={setNoteDialogOpen}
              setShiftDialogOpen={setShiftOpenInternal}
              previousNextCallbacks={previousNextCallbacks}
            />
          </Box>
          <Swipe
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: '1 1 auto',
              overflowY: 'auto',
            }}
            onSwipeRight={onSwipeRight}
            onSwipeLeft={onSwipeLeft}
            shouldBlockSwiping={shouldBlockSwiping}>
            <Grid
              sx={{
                maxHeight: window.innerHeight - 60 - (readOnly ? 0 : isSmallScreen ? 96 : 70),
                overflowX: 'auto',
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
              }}>
              <CalendarContent
                calendarLoading={calendarLoading}
                calendarView={calendarView}
                calendarItems={calendarItems}
                currentDate={currentDate}
                isEventBoxOpen={isEventBoxOpen}
                setIsEventBoxOpen={setIsEventBoxOpen}
              />
            </Grid>
          </Swipe>

          {isMediumScreen && (
            <CalendarSpeedDial
              open={speedDialOpen}
              setOpen={setSpeedDialOpen}
              setShiftDialogOpen={setShiftDialogOpen}
              setNoteDialogOpen={setNoteDialogOpen}
            />
          )}
        </Paper>
      </Grid>
    </>
  );
};

export default withAuthInfo(CalendarWrapper);
